import { Avatar, AvatarFallback, AvatarImage } from '@shared/components/ui/shadcn/avatar';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { StockMovementsModel, MovementEntity } from '../../models/StockMovements';

type EntityKey = 'stock_location_order' | 'returned_reception' | 'location_order' | 'entry' | 'extra_work_order';

const ENTITY_KEYS: EntityKey[] = [
  'stock_location_order',
  'returned_reception',
  'location_order',
  'entry',
  'extra_work_order',
];

const EMPTY = <span className="text-xs text-complementary-text flex items-center h-full">-</span>;

const linkClass = 'text-xs font-medium text-title truncate underline underline-offset-2 decoration-1';

function preparationUrl(entity: MovementEntity): string {
  const status =
    typeof entity.status === 'number' ? entity.status : entity.status ? parseInt(String(entity.status), 10) : NaN;
  if (!Number.isNaN(status) && (status === 0 || status === 1)) {
    return `/order-preparation/picking/${entity.id}`;
  }
  return `/order-preparation/packing/${entity.id}`;
}

function entityPath(key: EntityKey, entity: MovementEntity): string {
  switch (key) {
    case 'stock_location_order':
      return preparationUrl(entity);
    case 'returned_reception':
      return `/reused/${entity.id}/receive`;
    case 'location_order':
      return `/location-orders/${entity.id}`;
    case 'entry':
      return `/merchandise/view-entry/${entity.id}`;
    case 'extra_work_order':
      return `/extra-work-orders/${entity.id}`;
  }
}

function entityShortLabel(key: EntityKey, t: (key: string, fallback?: string) => string): string {
  const labels: Record<EntityKey, string> = {
    stock_location_order: t('table.preparation_short', 'Prep.'),
    returned_reception: t('table.returned_reception_short', 'Reh.'),
    location_order: t('table.location_order_short', 'Ubic.'),
    entry: t('table.entry_short', 'Ent.'),
    extra_work_order: t('table.extra_work_order_short', 'Extra'),
  };
  return labels[key];
}

function entityDisplayValue(key: EntityKey, entity: MovementEntity): string {
  if (key === 'entry') {
    return entity.reference || entity.code || entity.ref || `#${entity.id}`;
  }
  return entity.code || entity.ref || entity.reference || `#${entity.id}`;
}

/** Columna Pedido */
export function MovementOrderCell({ item }: { item: StockMovementsModel }) {
  const orderId = item.order?.id ?? item.order_id;
  if (!orderId) return EMPTY;

  const ref = item.order?.ref ?? `#${orderId}`;
  const to = `/orders/view/${orderId}`;
  const shopName = item.shop?.name;
  const initials = shopName
    ?.split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <Link
      to={to}
      className="inline-flex items-center gap-1.5 min-w-0 h-full max-w-full underline underline-offset-2 decoration-1 hover:opacity-80"
      data-testid={`movement-order-${item.id}`}
    >
      {item.shop?.image ? (
        <Avatar className="h-4 w-4 shrink-0 bg-card-bg light:bg-hover-press">
          <AvatarImage src={item.shop.image} alt={shopName ?? ref} />
          <AvatarFallback className="text-[8px]">{initials}</AvatarFallback>
        </Avatar>
      ) : null}
      <span className="min-w-0 truncate leading-tight text-xs font-medium text-title">
        {ref}
        {shopName ? <span className="text-[11px] font-normal text-subtitle"> · {shopName}</span> : null}
      </span>
    </Link>
  );
}

function TaskLine({
  to,
  value,
  prefix,
  showPrefix,
}: {
  to: string;
  value: string;
  prefix?: string;
  showPrefix?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1 min-w-0 max-w-full">
      {showPrefix && prefix ? <span className="text-[10px] text-complementary-text shrink-0">{prefix}</span> : null}
      <Link to={to} className={linkClass}>
        {value}
      </Link>
    </span>
  );
}

/** Columna Tarea (preparación, entrada, rehusados, etc.) */
export function MovementTaskCell({ item }: { item: StockMovementsModel }) {
  const { t } = useTranslation('stockmovementsModule');
  const tr = (key: string, fallback?: string) => t(key, { defaultValue: fallback ?? key }).toString();

  const entries = ENTITY_KEYS.flatMap((key) => {
    const entity = item[key];
    return entity ? [{ key, entity }] : [];
  });

  if (entries.length > 0) {
    const showPrefix = entries.length > 1;
    const lines: ReactNode[] = entries.map(({ key, entity }) => (
      <TaskLine
        key={key}
        to={entityPath(key, entity)}
        value={entityDisplayValue(key, entity)}
        prefix={entityShortLabel(key, tr)}
        showPrefix={showPrefix}
      />
    ));

    return (
      <div className="flex h-full w-full min-w-0 items-center justify-start" data-testid={`movement-task-${item.id}`}>
        <div
          className={`flex min-w-0 w-full justify-start ${showPrefix ? 'flex-col items-start gap-0 leading-tight py-0.5' : ''}`}
        >
          {lines}
        </div>
      </div>
    );
  }

  return EMPTY;
}
